import { useAuth } from 'react-oidc-context'
import { useContext } from 'react'
import { useSnackbar } from 'notistack'
import { ApiContext } from '../contexts/ApiContext'
import { DocumentType } from '@kistill/shared/types'

interface UseListViewDocumentProps {
  title?: string
  urlRoute?: string
}

export const useListViewDocument = ({
  title,
  urlRoute,
}: UseListViewDocumentProps) => {
  const { user } = useAuth()
  const apiPath = useContext(ApiContext)
  const { enqueueSnackbar } = useSnackbar()

  const handleOpenDocument = async (
    enumType: DocumentType,
    selected: readonly string[],
    totalGuids: string[],
  ) => {
    // Determine which guids to use based on whether any items are selected
    const guids = selected.length > 0 ? selected : totalGuids

    const fileType =
      enumType === 'EXCEL' ? 'xlsx' : enumType === 'WORD' ? 'docx' : null

    // If the route is for items, there is already a parameter in the url
    const url = urlRoute?.includes('ItemList?type')
      ? `${apiPath}/api/${urlRoute}&documentType=${enumType}`
      : `${apiPath}/api/${urlRoute}?documentType=${enumType}`

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.access_token}`,
        },
        body: JSON.stringify(guids),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('HTTP error:', response.status, errorText)
        enqueueSnackbar(
          `Villa kom upp við að sækja skjal: ${response.status} ${response.statusText}`,
          { variant: 'error' },
        )
        return
      }

      const blob = await response.blob()

      // Create blob link to download
      const blobUrl = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')

      link.href = blobUrl

      const today = new Date()
      const year = today.getFullYear()
      const month = today.getMonth()
      const day = today.getDate()

      link.setAttribute(
        'download',
        `${title} ${`${day.toString().padStart(2, '0')}.${(month + 1)
          .toString()
          .padStart(2, '0')}.${year}`}.${fileType}`,
      )

      // Append to html link element page
      document.body.appendChild(link)

      // Start download
      link.click()

      // Clean up and remove the link
      document.body.removeChild(link)
      link.remove()

      enqueueSnackbar(
        `Skjal fyrir "${title}" sótt með ${
          selected.length > 0 ? selected.length : totalGuids.length
        } stökum.`,
        { variant: 'success' },
      )
    } catch (error) {
      console.error('There was an error with the fetch operation: ', error)
      enqueueSnackbar('Villa kom upp við að sækja skjal', { variant: 'error' })
    }
  }

  return {
    handleOpenDocument,
  }
}
